import React from "react";
import { Box, Typography } from "@mui/material";
import { Chart as ChartJS, ArcElement } from "chart.js";
import { Doughnut } from "react-chartjs-2";

import { CUSTOM_COLORS } from "constants/colors";

const { purple } = CUSTOM_COLORS;

ChartJS.register(ArcElement);

const backgroundPlugin = {
  id: "background",
  beforeDraw: (chart) => {
    const { ctx, width, height } = chart;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2; // Donut radius

    // Donut background
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = purple[200];
    ctx.fill();

    // Donut inner circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 0.81, 0, 2 * Math.PI);
    ctx.fillStyle = "#fff";
    ctx.fill();
  },
};

const chartOptions = {
  rotation: 175,
  responsive: true,
  cutout: "80%",
  plugins: {
    // To hide the legend and the tooltip by default
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  maintainAspectRatio: true, // To keep the aspect ratio of the chart
};

/**
 * BilledHoursChart component is a component that displays the billed hours and the remaining authorized hours.
 *
 * @param {Object} props - The component props.
 * @param {number} props.value - The number of billed hours in percentage. (0-100)
 * @param {string} props.color - The color of the chart element. (default: "#333")
 * @returns {React.ReactNode} The BilledHoursChart component.
 */
const BilledHoursChart = ({ value = 0, color = "#333" }) => {
  const progressBarValue = value > 100 ? 100 : value;
  const remainingAuthorizedHours = 100 - progressBarValue;

  // if value is greater than or equal to 100, the border radius should be 0
  const borderRadius = value >= 100 ? 0 : 30;

  const data = {
    labels: ["Billed hours", "Remaining authorized hours"],
    datasets: [
      {
        label: " ",
        data: [progressBarValue, remainingAuthorizedHours],
        backgroundColor: [color, "transparent"],
        borderRadius,
        borderWidth: 0,
      },
    ],
  };

  return (
    <Box width={160} height={160} position="relative">
      <Box
        width={160}
        height={160}
        position="absolute"
        top={0}
        left={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          component="p"
          variant="h2"
          color={color}
          fontFamily="TestTiemposText"
          fontWeight={500}
        >
          {value.toFixed(0)}%
        </Typography>
      </Box>
      <Doughnut data={data} options={chartOptions} plugins={[backgroundPlugin]} />
    </Box>
  );
};

export default BilledHoursChart;
